import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, Edit3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PRICING_TYPES = [
    { value: "per_unit", label: "Por unidade" },
    { value: "per_seat", label: "Por lugar" },
    { value: "per_face", label: "Por face" },
    { value: "per_m2", label: "Por metro quadrado" },
    { value: "per_qty", label: "Por quantidade" },
    { value: "on_request", label: "Sob avaliação" },
];

export default function AdminServices() {
    const [services, setServices] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const load = async () => {
        const { data } = await api.get("/admin/services");
        setServices(data);
    };

    useEffect(() => { load(); }, []);

    const openNew = () => {
        setEditing({ key: "", name: "", description: "", active: true, order: services.length + 1, pricing_type: "per_unit", base_price: 0, duration_minutes: 60, icon: "sparkles" });
        setOpen(true);
    };

    const openEdit = (s) => { setEditing({ ...s }); setOpen(true); };

    const save = async () => {
        try {
            if (services.find((x) => x.id === editing.id)) {
                await api.put(`/admin/services/${editing.id}`, editing);
            } else {
                await api.post("/admin/services", editing);
            }
            toast.success("Serviço guardado");
            setOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminar este serviço?")) return;
        await api.delete(`/admin/services/${id}`);
        load();
    };

    const toggleActive = async (s) => {
        await api.put(`/admin/services/${s.id}`, { ...s, active: !s.active });
        load();
    };

    return (
        <div className="space-y-6" data-testid="admin-services">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight">Serviços</h1>
                    <p className="text-white/60">Gerir catálogo e preços base.</p>
                </div>
                <Button onClick={openNew} className="ab-btn-primary rounded-full" data-testid="add-service">
                    <Plus className="w-4 h-4 mr-2" /> Novo serviço
                </Button>
            </div>

            <div className="ab-card overflow-hidden">
                <table className="w-full">
                    <thead className="bg-[#171717] border-b border-[#2A2A2A] text-xs uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left p-4">Chave</th>
                            <th className="text-left p-4">Nome</th>
                            <th className="text-left p-4">Tipo de preço</th>
                            <th className="text-right p-4">Preço base</th>
                            <th className="text-center p-4">Ativo</th>
                            <th className="text-right p-4">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2A2A2A]">
                        {services.map((s) => (
                            <tr key={s.id} className="hover:bg-[#171717]">
                                <td className="p-4 font-mono text-sm">{s.key}</td>
                                <td className="p-4 font-medium">{s.name}</td>
                                <td className="p-4 text-sm">{PRICING_TYPES.find((t) => t.value === s.pricing_type)?.label || s.pricing_type}</td>
                                <td className="p-4 text-right font-heading font-bold">{Number(s.base_price).toFixed(2)} €</td>
                                <td className="p-4 text-center">
                                    <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`toggle-${s.key}`} />
                                </td>
                                <td className="p-4 text-right">
                                    <div className="flex gap-2 justify-end">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-${s.key}`}><Edit3 className="w-4 h-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid={`delete-${s.key}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? "Editar serviço" : "Novo serviço"}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Chave</Label>
                                    <Input value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value.toLowerCase() })} disabled={!!editing.id} />
                                </div>
                                <div>
                                    <Label>Nome</Label>
                                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <Label>Descrição</Label>
                                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Tipo de preço</Label>
                                    <Select value={editing.pricing_type} onValueChange={(v) => setEditing({ ...editing, pricing_type: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {PRICING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Preço base (€)</Label>
                                    <Input type="number" step="0.01" value={editing.base_price} onChange={(e) => setEditing({ ...editing, base_price: Number(e.target.value) })} />
                                </div>
                                <div>
                                    <Label>Duração (min)</Label>
                                    <Input type="number" value={editing.duration_minutes} onChange={(e) => setEditing({ ...editing, duration_minutes: Number(e.target.value) })} />
                                </div>
                                <div>
                                    <Label>Ordem</Label>
                                    <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} />
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                                <Label>Ativo</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} className="ab-btn-primary" data-testid="save-service"><Save className="w-4 h-4 mr-2" />Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
